/**
 * NewsFeedWidget - News feed with slider layout
 *
 * Displays news articles in a horizontal slider (cloned from NewsSourcesCarousel)
 * Returns null if only 1 article or no articles
 */

import { useMemo, useState, useRef, useEffect } from 'react';

/**
 * Format relative time
 */
function formatRelativeTime(timestamp) {
    if (!timestamp) return '';

    const now = Date.now();
    const date = typeof timestamp === 'number'
        ? (timestamp < 1e12 ? timestamp * 1000 : timestamp)
        : new Date(timestamp).getTime();

    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Vừa xong';
    if (minutes < 60) return `${minutes} phút trước`;
    if (hours < 24) return `${hours} giờ trước`;
    if (days < 7) return `${days} ngày trước`;

    return new Date(date).toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
}

/**
 * Normalize article fields (handle both API format and normalized format)
 */
function normalizeArticle(article) {
    if (!article) return article;

    // Parse tickers if it's a JSON string
    let tickers = article.tickers || [];
    if (typeof tickers === 'string') {
        try {
            tickers = JSON.parse(tickers);
        } catch (e) {
            tickers = [];
        }
    }
    if (!Array.isArray(tickers)) {
        tickers = [];
    }

    return {
        id: article.id,
        title: article.title,
        summary: article.summary || article.description || article.aiContent,
        imageUrl: article.imageUrl || article.thumbUrl,
        url: article.url || article.sourceUrl,
        source: article.source || article.sourceWebsiteUrl,
        publishedAt: article.publishedAt || article.ts,
        category: article.category,
        tickers: tickers,
        aiSentiment: article.aiSentiment
    };
}

/**
 * Get favicon URL from source domain
 */
function getFaviconUrl(sourceUrl) {
    if (!sourceUrl) return null;
    try {
        let domain = sourceUrl;
        if (sourceUrl.includes('://')) {
            domain = new URL(sourceUrl).hostname;
        } else if (!sourceUrl.includes('.')) {
            return null;
        }
        return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
    } catch {
        return null;
    }
}

/**
 * Main NewsFeedWidget component - Slider only
 */
export default function NewsFeedWidget({ data, config }) {
    const scrollContainerRef = useRef(null);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const [showLeftArrow, setShowLeftArrow] = useState(false);

    // Extract config
    const { ids = [] } = config?.params || config || {};

    // Process articles from data
    const articles = useMemo(() => {
        let result = [];

        if (!data) return result;

        // If data is array, use directly
        if (Array.isArray(data)) {
            result = ids.length > 0
                ? data.filter(a => ids.includes(a.id))
                : data;
        }
        // If data has articles field
        else if (data.articles) {
            result = ids.length > 0
                ? data.articles.filter(a => ids.includes(a.id))
                : data.articles;
        }
        // If data is keyed by ID
        else if (ids.length > 0) {
            result = ids.map(id => data[id]).filter(Boolean);
        }
        else {
            result = Object.values(data);
        }

        // Sort by time - newest first
        return [...result].sort((a, b) => {
            const timeA = a.publishedAt || a.ts || 0;
            const timeB = b.publishedAt || b.ts || 0;
            // Convert to timestamps for comparison
            const tsA = typeof timeA === 'number' ? timeA : new Date(timeA).getTime();
            const tsB = typeof timeB === 'number' ? timeB : new Date(timeB).getTime();
            return tsB - tsA; // Newest first
        });
    }, [data, ids]);

    // Handle article click
    const handleArticleClick = (article) => {
        if (article.url) {
            window.open(article.url, '_blank', 'noopener,noreferrer');
        }
    };

    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setShowLeftArrow(scrollLeft > 10);
            setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 10);
        }
    };

    useEffect(() => {
        handleScroll();
    }, [articles]);

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const containerWidth = container.clientWidth;
            const maxScroll = container.scrollWidth - container.clientWidth;
            const currentScroll = container.scrollLeft;
            const remainingScroll = maxScroll - currentScroll;
            const actualScroll = Math.min(containerWidth, remainingScroll);
            container.scrollBy({ left: actualScroll, behavior: 'smooth' });
        }
    };

    const scrollLeftFn = () => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const containerWidth = container.clientWidth;
            const currentScroll = container.scrollLeft;
            const actualScroll = Math.min(containerWidth, currentScroll);
            container.scrollBy({ left: -actualScroll, behavior: 'smooth' });
        }
    };

    // Don't render if no articles or only 1 article
    if (!articles || articles.length <= 1) {
        return null;
    }

    return (
        <div className="relative">
            {/* Cards Container */}
            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="flex gap-3 overflow-x-auto scroll-smooth scrollbar-hide pb-2 -mb-2"
                style={{ scrollSnapType: 'x mandatory' }}
            >
                {articles.map((rawArticle, index) => {
                    const article = normalizeArticle(rawArticle);
                    const { id, title, imageUrl, source, publishedAt, url } = article;
                    const faviconUrl = getFaviconUrl(source);

                    return (
                        <a
                            key={id || index}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all news-item"
                            style={{ scrollSnapAlign: 'start' }}
                            onClick={(e) => {
                                e.preventDefault();
                                handleArticleClick(article);
                            }}
                        >
                            {/* Image */}
                            <div className="relative w-full h-[100px] sm:h-[157px] bg-gray-100 dark:bg-gray-700 rounded-t-xl overflow-hidden">
                                {imageUrl ? (
                                    <img
                                        src={imageUrl}
                                        alt={title}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600">
                                        <span className="text-white text-2xl font-bold">
                                            {source?.charAt(0) || '?'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Content */}
                            <div className="p-3">
                                {/* Source with favicon */}
                                <div className="flex items-center gap-2 mb-2">
                                    {faviconUrl && (
                                        <img
                                            src={faviconUrl}
                                            alt=""
                                            className="w-4 h-4 rounded-full flex-shrink-0"
                                            style={{ backgroundColor: '#fff' }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    )}
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                        {source || 'Unknown'}
                                    </span>
                                </div>

                                {/* Title */}
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2 mb-1" style={{ lineHeight: '1.4' }}>
                                    {title}
                                </h3>

                                {/* Timestamp */}
                                {publishedAt && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {formatRelativeTime(publishedAt)}
                                    </span>
                                )}
                            </div>
                        </a>
                    );
                })}
            </div>

            {/* Left Arrow */}
            {showLeftArrow && (
                <button
                    onClick={scrollLeftFn}
                    className="hidden sm:flex absolute left-0 top-1/2 w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-md items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors z-10"
                    style={{ border: '1px solid rgba(13, 13, 13, 0.1)', transform: 'translateY(-50%) translateX(-50%)' }}
                >
                    <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
            )}

            {/* Right Arrow */}
            {showRightArrow && articles.length > 1 && (
                <button
                    onClick={scrollRight}
                    className="hidden sm:flex absolute right-0 top-1/2 w-8 h-8 bg-white dark:bg-gray-800 rounded-full shadow-md items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors z-10"
                    style={{ border: '1px solid rgba(13, 13, 13, 0.1)', transform: 'translateY(-50%) translateX(50%)' }}
                >
                    <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            )}
        </div>
    );
}
